import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import APIService from "../../Service/APIService";
import Card from "../../common/card";
import LastOrderCard from "../../common/last-order-card";
import "../Distributor/DistributorHomeTab.css";

const DistributorHomeTab = ({ filterButton }) => {

  const formatCurrency = (value = 0) =>
    `$${Number(value).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })}`;
  const { id } = useParams();
  const isYTD = filterButton === "YTD";

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    salesYTD: 0,
    currentSalesYTD: 0,
    salesMTD: 0,
    currentSalesMTD: 0,
    totalAccounts: 0,
    totalActiveAccounts: 0,
    openBalance: 0,
    openBalanceInvoices: 0,
    balancePastDue: 0,
    invoicesPastDue: 0,
    openBalanceInvoicesData: [],
    daysSinceLastDirectOrder: null,
    lastDirectOrderDate: null,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          `getdistributorshometab?ID=${id}`
        );

        const data = response?.data?.data || {};

        const formattedInvoices =
          data?.OpenBalanceInvoices?.map((invoice) => {
            const daysDiff = moment(invoice.InvoiceDueDate).diff(
              moment(),
              "days"
            );

            return {
              ...invoice,
              DueIn: daysDiff < 0 ? "Past Due" : `${daysDiff + 1} days`,
              InvoiceDate: moment(invoice.InvoiceDate).format("MM-DD-YYYY"),
              InvoiceDueDate: moment(invoice.InvoiceDueDate).format(
                "MM-DD-YYYY"
              ),
              OrderDate: moment(invoice.OrderDate).format("MM-DD-YYYY"),
              InvoiceTotal: formatCurrency(invoice.InvoiceTotal),
              OpenBalance: formatCurrency(
                invoice.InvoiceTotal +
                (invoice.ShippingCost || 0) -
                invoice.TotalPaymentsApplied
              ),
            };
          }) || [];

        setDashboardData({
          salesYTD: data?.TotalSalesYTD || 0,
          currentSalesYTD: data?.CurrentSalesYTD || 0,
          salesMTD: data?.TotalSalesMTD || 0,
          currentSalesMTD: data?.CurrentSalesMTD || 0,
          totalAccounts: data?.TotalAccountsCount || 0,
          totalActiveAccounts: data?.TotalActiveAccountsCount || 0,
          openBalance: data?.TotalOpenBalance || 0,
          openBalanceInvoices: data?.TotalOpenBalanceInvoiceCount || 0,
          balancePastDue: data?.TotalOpenBalancePastDue || 0,
          invoicesPastDue: data?.TotalPastDueInvoiceCount || 0,
          openBalanceInvoicesData: formattedInvoices,
          daysSinceLastDirectOrder: data?.DaysSinceLastDirectOrder ?? null,
          lastDirectOrderDate: data?.LastDirectOrderDate ?? null,
        });
      } catch (error) {
        console.error("Failed to load distributor dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [id]);

  const currentSales = useMemo(
    () =>
      isYTD
        ? dashboardData.currentSalesYTD
        : dashboardData.currentSalesMTD,
    [isYTD, dashboardData]
  );

  const realizedSales = useMemo(
    () => (isYTD ? dashboardData.salesYTD : dashboardData.salesMTD),
    [isYTD, dashboardData]
  );

  return (
    <div className="home-card-wrapper">
      <Card
        loading={loading}
        cardTitle="Current Sales"
        cardTitleValue={currentSales}
        cardBottomNumber={dashboardData.totalActiveAccounts}
        cardBottomName="Accounts"
        hideBottomBar
      />

      <Card
        loading={loading}
        cardTitle="Realized Sales"
        cardTitleValue={realizedSales}
        cardBottomNumber={dashboardData.totalActiveAccounts}
        cardBottomName="Accounts"
      />

      <Card
        loading={loading}
        cardTitle="Open Balance"
        cardTitleValue={dashboardData.openBalance}
        cardBottomNumber={dashboardData.openBalanceInvoices}
        cardBottomName="Invoices"
      />

      <Card
        loading={loading}
        cardTitle="Balance Past Due"
        cardTitleValue={dashboardData.balancePastDue}
        cardTitleValueColor="failureColor"
        cardBottomNumber={dashboardData.invoicesPastDue}
        cardBottomName="Invoices"
      />

      <LastOrderCard
        loading={loading}
        daysSinceLastOrder={dashboardData.daysSinceLastDirectOrder}
        lastOrderDate={dashboardData.lastDirectOrderDate}
      />
    </div>
  );
};

export default DistributorHomeTab;
